// React and Redux
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// External libraries

import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { List, Range } from 'immutable';
import styled from 'styled-components';

// UI Components
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILoadingDots from 'UIComponents/loading/UILoadingDots';
import UIOverlay from 'UIComponents/overlay/UIOverlay';
import UIOverlayContent from 'UIComponents/overlay/UIOverlayContent';
import { getEditLinkURL as getEditLinkURLSelector, getUserTimezone, getEvent as getEventSelector, getHasCustomAvailability, getHasMoreLinkAvailability, getIsProcessingLinkAvailability, getCustomAvailabilityStartDate, getIsUngatedForAvaialabilityRequestMonthOne, getPushbackTime
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableDates as getAvailableDatesSelector, isSelectedDateAvailable as getIsSelectedDateAvailable
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/RequestStatusSelectors';
import { getCurrentTime
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/RESTParamSelectors';
import { getConfiguredAvailabilityByDate, getTroubleshootTimesForSelectedDurationByDay, getConfiguredBookableDates, getMinimumNoticeTimeBusyDays, isTroubleshootingSelector
// @ts-expect-error untyped yet
} from 'MeetingsLib/selectors/TroubleshootingSelectors';

// MeetingsLib components
// @ts-expect-error untyped yet
import DatePickerButton from 'MeetingsLib/components/schedule/date/DatePickerButton';
import DatePickerHeader from './DatePickerHeaderV2';
import { HiddenAriaStatus
// @ts-expect-error untyped yet
} from 'MeetingsLib/components/decorative/HiddenAriaStatus';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';

// Contexts for PublicAvailabilityInfo and TroubleshootTimes
import { useIsLoadingLinkAvailabilityPage, useSetMonthOffset } from 'MeetingsLib/context/PublicAvailabilityInfoContext';
import { useIsLoadingTroubleshootPage, useSetTroubleshootMonthOffset } from 'MeetingsLib/context/TroubleshootTimesContext';

// MeetingsBase Models and Utilities
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import { getPrimaryColorOrDefault } from 'MeetingsBase/utils/color';

// Types
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTable = styled.table.withConfig({
  displayName: "DatePickerV2__StyledTable",
  componentId: "st1vmr-0"
})(["table-layout:fixed;width:100%;margin:0;"]);
const StyledCaption = styled.caption.withConfig({
  displayName: "DatePickerV2__StyledCaption",
  componentId: "st1vmr-1"
})(["font-size:0;line-height:0;"]);
const StyledOverlay = styled(UIOverlay).withConfig({
  displayName: "DatePickerV2__StyledOverlay",
  componentId: "st1vmr-2"
})(["background-color:rgba(45,62,80,0) !important;"]);
const StyledNoAvailableDatesOverlay = styled(UIOverlay).withConfig({
  displayName: "DatePickerV2__StyledNoAvailableDatesOverlay",
  componentId: "st1vmr-3"
})(["background-color:rgba(45,62,80,0) !important;margin-left:", " !important;margin-right:", " !important;"], props => props.marginleft ? props.marginleft : '65px', props => props.marginright ? props.marginright : '80px');
const StyledNextArrowIcon = styled(UIIcon).withConfig({
  displayName: "DatePickerV2__StyledNextArrowIcon",
  componentId: "st1vmr-4"
})(["font-size:16px !important;"]);
const StyledDatesContainer = styled.div.withConfig({
  displayName: "DatePickerV2__StyledDatesContainer",
  componentId: "st1vmr-5"
})(["position:relative;"]);
const NUM_DAYS = 7;
const NUM_WEEKS = 5;
const DAY_RANGE = Range().take(NUM_DAYS).toArray();
function isDateInMonth(momentDate, momentMonth) {
  return momentMonth.month() === momentDate.month();
}
function getWeekRange() {
  return Range().take(NUM_WEEKS + 1).toArray();
}
function getDateStringsBetween(startDate, endDate) {
  const currentDate = startDate.clone();
  const dates = [];
  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate.add(1, 'day');
  }
  return dates;
}
const NoAvailableDates = ({
  getNextMonth,
  hasMoreAvailability,
  isTroubleshooting,
  startOfMonth,
  endOfMonth,
  availableDateOptions,
  isProcessingLinkAvailability,
  renderedMonthLabelRef,
  brandingTextStyle
}) => {
  if (isProcessingLinkAvailability) {
    return null;
  }
  const datesWithinMonth = getDateStringsBetween(startOfMonth, endOfMonth);
  const shouldRenderNextMonthCTA = !datesWithinMonth.some(dateString => availableDateOptions.includes(dateString));
  const noDatesAvailable = availableDateOptions.length === 0 && !hasMoreAvailability;
  if (!shouldRenderNextMonthCTA || isTroubleshooting) {
    return null;
  }
  if (renderedMonthLabelRef.current && renderedMonthLabelRef.current.innerText !== startOfMonth.format('MMMM YYYY')) {
    return null;
  }
  return /*#__PURE__*/_jsx(StyledNoAvailableDatesOverlay, {
    contextual: true,
    width: "100%",
    "data-test-id": "no-dates-available-container",
    marginleft: noDatesAvailable ? '85px' : undefined,
    children: /*#__PURE__*/_jsxs(UIOverlayContent, {
      children: [/*#__PURE__*/_jsx("b", {
        style: brandingTextStyle,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.noPreviousMonthReason",
          options: {
            month: startOfMonth.format('MMMM')
          }
        })
      }), !noDatesAvailable && /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        onClick: getNextMonth,
        "data-test-id": "getNextMonth-btn",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.nextMonthCTA"
        }), /*#__PURE__*/_jsx(StyledNextArrowIcon, {
          name: "right"
        })]
      })]
    })
  }, startOfMonth.format('MMMM'));
};
const propTypes = {
  // passed
  branding: PropTypes.instanceOf(Branding).isRequired,
  compact: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile),
  setEventDate: PropTypes.func.isRequired,
  DatePickerMarker: PropTypes.func.isRequired,
  TimePickerMarker: PropTypes.func.isRequired,
  isPreviewed: PropTypes.bool
};
function DatePicker({
  branding,
  compact,
  profile,
  setEventDate,
  DatePickerMarker,
  TimePickerMarker,
  isPreviewed
}) {
  const bookableDates = useSelector(getConfiguredBookableDates);
  const configuredAvailabilityByDate = useSelector(getConfiguredAvailabilityByDate);
  const hasCustomAvailability = useSelector(getHasCustomAvailability);
  const hasMoreAvailability = useSelector(getHasMoreLinkAvailability);
  const customAvailabilityStartDate = useSelector(getCustomAvailabilityStartDate);
  const currentTime = useSelector(getCurrentTime);
  const availableDateOptions = useSelector(getAvailableDatesSelector);
  const isSelectedDateAvailable = useSelector(getIsSelectedDateAvailable);
  const editLinkURL = useSelector(getEditLinkURLSelector);
  const troubleshootTimesForSelectedDurationByDay = useSelector(getTroubleshootTimesForSelectedDurationByDay);
  const isFetchingPublicAvailability = useSelector(getIsFetchingPublicAvailability);
  const minimumNoticeTimeBusyDays = useSelector(getMinimumNoticeTimeBusyDays);
  const pushbackTime = useSelector(getPushbackTime);
  const isTroubleshooting = useSelector(isTroubleshootingSelector);
  const userTimezone = useSelector(getUserTimezone);
  const selectedDate = useSelector(state => getEventSelector(state).dateString);
  const setMonthOffset = useSetMonthOffset();
  const setTroubleshootMonthOffset = useSetTroubleshootMonthOffset();
  // determines when linkAvailability processing is complete
  const isProcessingLinkAvailability = useSelector(getIsProcessingLinkAvailability);
  const isUngatedForAvailabilityRequestMonthOne = useSelector(getIsUngatedForAvaialabilityRequestMonthOne);
  const isLoadingLinkAvailabilityPage = useIsLoadingLinkAvailabilityPage();
  const isLoadingTroubleshootPage = useIsLoadingTroubleshootPage();
  const isLoadingPage = isLoadingLinkAvailabilityPage || isLoadingTroubleshootPage;
  const [monthsAhead, setMonthsAhead] = useState(0);
  const renderedMonthLabelRef = useRef(null);
  const hasAutomaticallySkippedMonthOneRef = useRef(false);

  /**
   * Base month needs to be the current date because
   * it is possible for no availability to be available from now into future months
   * We do not want a future month i.e availableDateOptions.min() to be the base month
   * unless custom availability has been set
   */
  const baseMonth = useMemo(() => {
    if (hasCustomAvailability && !isTroubleshooting) {
      // Ensure that custom availability is not in the past
      const baseDateForCustomAvailability = Math.max(customAvailabilityStartDate, Date.now() + pushbackTime);
      // custom availability start date is persisted to the BE in UTC
      // If we do not parse in UTC we could end up being a day behind and essentially a month behind
      return I18n.moment(baseDateForCustomAvailability).utc();
    }
    return I18n.moment();
  }, [isTroubleshooting, hasCustomAvailability, customAvailabilityStartDate, pushbackTime]);
  const startOfMonth = useMemo(() => {
    return I18n.moment(baseMonth).startOf('month').add(monthsAhead, 'months');
  }, [baseMonth, monthsAhead]);
  const endOfMonth = useMemo(() => {
    return I18n.moment(baseMonth).add(monthsAhead, 'months').endOf('month');
  }, [baseMonth, monthsAhead]);
  const availableDateOptionsForCurrentMonth = useMemo(() => {
    const startOfMonthLocaleString = startOfMonth.format('YYYY-MM');
    const availableDateArray = availableDateOptions.toArray();
    const availableDatesForStartOfMonth = availableDateArray.filter(dateString => dateString.includes(startOfMonthLocaleString));
    return availableDatesForStartOfMonth;
  }, [startOfMonth, availableDateOptions]);

  /**
   * We are using this to mitigate a paper cut where users are shown the
   * no availablility message at the end of the month
   * We may get rid of this entirely or put it in a setting
   */
  useEffect(() => {
    if (isUngatedForAvailabilityRequestMonthOne && monthsAhead === 0 && !availableDateOptionsForCurrentMonth.length && hasMoreAvailability) {
      if (!hasAutomaticallySkippedMonthOneRef.current) {
        setMonthsAhead(1);
        hasAutomaticallySkippedMonthOneRef.current = true;
      }
    }
  }, [availableDateOptionsForCurrentMonth.length, hasMoreAvailability, isUngatedForAvailabilityRequestMonthOne, monthsAhead]);
  const noAvailabilityInMobileView = useMemo(() => {
    return compact && !availableDateOptions.count() && !isFetchingPublicAvailability && !hasMoreAvailability;
  }, [availableDateOptions, compact, hasMoreAvailability, isFetchingPublicAvailability]);
  const localeData =
  // @ts-expect-error locale not typed
  I18n.moment.localeData(I18n.locale) || I18n.moment.localeData();
  // some locales start their week on Monday
  // @ts-expect-error _week may not be typed
  const daysToShiftForward = localeData._week.dow;
  const getDaysToShiftBack = useCallback(dateObject => {
    const daysFromSunday = dateObject.isoWeekday() % 7;

    /*
     * if the month starts on Sunday and we are in a locale
     * that starts weeks on Monday, we would cut off the first
     * Sunday unless we rendered the entire previous week
     */
    if (daysFromSunday === 0 && daysToShiftForward === 1) {
      return 6;
    }
    return daysFromSunday - daysToShiftForward;
  }, [daysToShiftForward]);
  const getStartOfWeek = useCallback(dateObject => {
    const daysToShiftBack = getDaysToShiftBack(dateObject);
    return I18n.moment(dateObject).subtract(daysToShiftBack, 'days');
  }, [getDaysToShiftBack]);
  const getNextMonth = useCallback(() => {
    setMonthsAhead(_monthsAhead => _monthsAhead + 1);
  }, [setMonthsAhead]);
  const startOfWeek = useMemo(() => getStartOfWeek(startOfMonth), [getStartOfWeek, startOfMonth]);
  useEffect(() => {
    if (!isPreviewed) {
      setMonthOffset(monthsAhead);
    }
  }, [isPreviewed, monthsAhead, setMonthOffset]);
  useEffect(() => {
    if (isTroubleshooting) {
      setTroubleshootMonthOffset(monthsAhead);
    }
  }, [isTroubleshooting, setTroubleshootMonthOffset, monthsAhead]);
  useEffect(() => {
    if (!compact && !isSelectedDateAvailable) {
      setEventDate(availableDateOptions.min());
    }
  }, [compact, isSelectedDateAvailable, availableDateOptions, setEventDate]);
  const handleSetEventDate = useCallback(date => {
    if (isTroubleshooting || availableDateOptions.includes(date)) {
      setEventDate(date);
    }
  }, [isTroubleshooting, availableDateOptions, setEventDate]);
  const brandingTextStyle = useMemo(() => {
    const {
      primaryColor
    } = branding;
    if (!primaryColor) {
      return null;
    }
    return {
      color: getPrimaryColorOrDefault(primaryColor)
    };
  }, [branding]);
  const abbreviatedWeekdays = useMemo(() => {
    // @ts-expect-error untyped yet
    return List(localeData._weekdaysShort);
  }, [localeData]);
  const daysToShift = useMemo(() => {
    return abbreviatedWeekdays.take(daysToShiftForward);
  }, [abbreviatedWeekdays, daysToShiftForward]);
  const weekdayNames = useMemo(() => {
    return abbreviatedWeekdays.splice(0, daysToShiftForward).concat(daysToShift);
  }, [abbreviatedWeekdays, daysToShiftForward, daysToShift]);
  const renderDays = weekNumber => {
    return DAY_RANGE.map(daysToAdd => {
      const date = I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }).format('YYYY-MM-DD');
      const isDateInCurrentMonth = isDateInMonth(I18n.moment(startOfWeek).add({
        days: daysToAdd,
        weeks: weekNumber
      }), startOfMonth);
      return /*#__PURE__*/_jsx(DatePickerButton, {
        daysToAdd: daysToAdd,
        branding: branding,
        currentTime: currentTime,
        date: date,
        isDateInCurrentMonth: isDateInCurrentMonth,
        availableDates: availableDateOptions,
        handleSetEventDate: handleSetEventDate
        // troubleshoot mode
        ,
        bookableDates: bookableDates,
        configuredAvailabilityByDate: configuredAvailabilityByDate,
        editLinkURL: editLinkURL,
        troubleshootTimesForSelectedDurationByDay: troubleshootTimesForSelectedDurationByDay,
        minimumNoticeTimeBusyDays: minimumNoticeTimeBusyDays,
        isTroubleshooting: isTroubleshooting,
        userTimezone: userTimezone,
        isLoadingLinkAvailabilityPage: isLoadingPage
      }, `${date}-${daysToAdd}`);
    });
  };
  function renderWeeksOrEmpty() {
    if (noAvailabilityInMobileView) {
      // Render empty state for compact view
      return /*#__PURE__*/_jsx("div", {
        className: "align-center m-top-12",
        children: /*#__PURE__*/_jsx(ZeroState, {
          profile: profile,
          TimePickerMarker: TimePickerMarker
        })
      });
    }
    const currentMonth = startOfMonth.format('MMMM');
    const weekDaysClasses = "title-case " + (compact ? 'm-bottom-2' : 'm-bottom-4');
    const weeks = getWeekRange().map(weeksToAdd => {
      return /*#__PURE__*/_jsx("tr", {
        className: "extraWeek",
        children: renderDays(weeksToAdd)
      }, weeksToAdd);
    });
    return /*#__PURE__*/_jsxs(StyledTable, {
      children: [/*#__PURE__*/_jsx(StyledCaption, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "prebook.datePicker.caption",
          options: {
            currentMonth
          }
        })
      }), /*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx("tr", {
          className: weekDaysClasses,
          children: weekdayNames.map(dayName => {
            return /*#__PURE__*/_jsx("th", {
              scope: "col",
              children: /*#__PURE__*/_jsx("span", {
                className: "is--heading-6",
                children: dayName
              })
            }, dayName);
          })
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: weeks
      })]
    });
  }
  const localizedDate = useMemo(() => {
    return I18n.moment(selectedDate).format('MMMM Do');
  }, [selectedDate]);
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    align: "center",
    className: "date-picker",
    children: [/*#__PURE__*/_jsx(DatePickerHeader, {
      startOfMonth: startOfMonth,
      availableDateOptions: availableDateOptions,
      compact: compact,
      isTroubleshooting: isTroubleshooting,
      setMonthsAhead: setMonthsAhead,
      branding: branding,
      isLoadingLinkAvailabilityPage: isLoadingPage,
      hasCustomAvailability: hasCustomAvailability,
      renderedMonthLabelRef: renderedMonthLabelRef,
      isPreviewed: isPreviewed
    }), /*#__PURE__*/_jsxs(StyledDatesContainer, {
      children: [renderWeeksOrEmpty(), !isLoadingPage && !noAvailabilityInMobileView && /*#__PURE__*/_jsx(NoAvailableDates, {
        getNextMonth: getNextMonth,
        hasMoreAvailability: hasMoreAvailability,
        isTroubleshooting: isTroubleshooting,
        startOfMonth: startOfMonth,
        isProcessingLinkAvailability: isProcessingLinkAvailability,
        endOfMonth: endOfMonth,
        availableDateOptions: availableDateOptionsForCurrentMonth,
        renderedMonthLabelRef: renderedMonthLabelRef,
        brandingTextStyle: brandingTextStyle
      }), isLoadingPage && /*#__PURE__*/_jsx(StyledOverlay, {
        contextual: true,
        width: "100%",
        children: /*#__PURE__*/_jsx(UILoadingDots, {
          "data-test-id": "loading-availability-dots"
        })
      })]
    }), /*#__PURE__*/_jsx(DatePickerMarker, {}), /*#__PURE__*/_jsx(HiddenAriaStatus, {
      className: "calendar-status",
      role: "status",
      "aria-live": "polite",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "accessible.calendarStatus",
        options: {
          selectedDate: localizedDate
        }
      })
    })]
  });
}
DatePicker.propTypes = propTypes;
export default DatePicker;